// ============================================
// PA Dashboard — Premium Glassmorphic UI
// ============================================

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import api from '../../services/api';
import socketService from '../../services/socket';
import Sidebar from '../../components/common/Sidebar';
import BottomNav from '../../components/common/BottomNav';
import DriverMobileHeader from '../../components/driver/DriverMobileHeader';
import { paMenuItems } from './paNav';
import { useToast } from '../../context/ToastContext';
import { useTheme } from '../../context/ThemeContext';
import decodeHtml from '../../utils/decodeHtml';
import NotificationBell from '../../components/common/NotificationBell';

const PADashboard = () => {
    const { user } = useSelector((state) => state.auth);
    const { theme, toggleTheme } = useTheme();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const { success, info } = useToast();

    const fetchDashboard = useCallback(async () => {
        try {
            setLoading(true);
            const res = await api.get('/pa/dashboard');
            setData(res.data.data);
        } catch (err) {
            console.error('PA dashboard error:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchDashboard();

        // ── Connect socket with PA user ID for targeted notifications ──
        if (user?._id) {
            socketService.connect('pa', user._id);
        }

        const handleDocUpdate = (d) => {
            fetchDashboard();
            if (d?.status === 'approved') success('Your document has been approved!');
            else if (d?.status === 'rejected') info('A document was returned. Please re-upload.');
        };
        
        const handleRouteUpdate = () => {
            fetchDashboard();
            info('Route assignments updated');
        };

        // ── Real-time: admin assigned this PA to a route ──
        const handleNotification = (n) => {
            fetchDashboard();
            if (n?.title) info(n.title);
        };

        socketService.on('pa:doc:updated', handleDocUpdate);
        socketService.on('route:updated', handleRouteUpdate);
        socketService.on('route:children:updated', handleRouteUpdate);
        socketService.on('notification:new', handleNotification);
        
        return () => {
            socketService.off('pa:doc:updated', handleDocUpdate);
            socketService.off('route:updated', handleRouteUpdate);
            socketService.off('route:children:updated', handleRouteUpdate);
            socketService.off('notification:new', handleNotification);
        };
    }, [fetchDashboard, success, info, user?._id]);

    const profile = data?.profile || {};
    const stats = data?.stats || {};

    const statCards = [
        { label: 'Total Docs', value: stats.totalDocs || 0, gradient: 'from-violet-500 to-purple-600', shadow: 'shadow-violet-500/20' },
        { label: 'Approved', value: stats.approved || 0, gradient: 'from-emerald-500 to-teal-600', shadow: 'shadow-emerald-500/20' },
        { label: 'Pending', value: stats.pending || 0, gradient: 'from-amber-500 to-orange-600', shadow: 'shadow-amber-500/20' },
        { label: 'Action Needed', value: (stats.rejected || 0) + (stats.notFiled || 0) + (stats.expired || 0), gradient: 'from-rose-500 to-red-600', shadow: 'shadow-rose-500/20' },
    ];

    return (
        <div className="flex flex-col sm:flex-row h-screen bg-slate-50 dark:bg-[#050510] text-slate-900 dark:text-white font-sans transition-colors duration-500 aurora-bg relative pb-20 sm:pb-0">
            <Sidebar items={paMenuItems} theme="violet" title="IdaCab PA" />

            <div className="flex-1 flex flex-col overflow-hidden">
                {/* Mobile Header — avatar dropdown with Settings + Logout */}
                <DriverMobileHeader
                    user={user}
                    theme={theme}
                    toggleTheme={toggleTheme}
                    settingsPath="/pa/settings"
                />

                {/* Desktop Header */}
                <header className="hidden sm:flex h-24 bg-white/40 dark:bg-slate-900/40 backdrop-blur-3xl items-center justify-between px-10 flex-shrink-0 z-50 relative border-b border-white/60 dark:border-white/5 shadow-sm dark:shadow-2xl transition-colors duration-500">
                    <div>
                        <h2 className="text-2xl font-black bg-gradient-to-r from-violet-600 to-purple-400 bg-clip-text text-transparent tracking-tighter">PA Dashboard</h2>
                        <p className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest mt-0.5">Passenger Assistant Portal</p>
                    </div>
                    {!loading && (
                        <div className="flex items-center gap-3">
                            <div className={`flex items-center gap-2 px-4 py-2 rounded-2xl border text-[10px] font-black uppercase tracking-widest ${profile.isVerified ? 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-200 dark:border-emerald-500/20' : 'bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-200 dark:border-amber-500/20'}`}>
                                <div className={`w-2 h-2 rounded-full ${profile.isVerified ? 'bg-emerald-500' : 'bg-amber-500 animate-pulse'}`} />
                                {profile.isVerified ? 'Verified' : 'Pending Verification'}
                            </div>
                            <NotificationBell />
                        </div>
                    )}
                </header>


                <main className="flex-1 overflow-y-auto p-4 sm:p-8 bg-transparent sidebar-scroll transition-colors duration-500">
                    <div className="max-w-5xl mx-auto space-y-8">

                        {/* Welcome Hero */}
                        <div className="relative bg-gradient-to-br from-violet-600 via-purple-600 to-indigo-700 dark:from-violet-900/80 dark:via-purple-900/80 dark:to-indigo-950/80 backdrop-blur-3xl p-8 sm:p-12 rounded-[2.5rem] text-white shadow-2xl shadow-violet-600/30 dark:shadow-black/50 border border-white/10 overflow-hidden group">
                            <div className="absolute -right-20 -top-20 w-72 h-72 bg-white/5 rounded-full blur-3xl group-hover:bg-white/10 transition-all duration-1000" />
                            <div className="absolute -left-16 -bottom-16 w-48 h-48 bg-violet-400/10 rounded-full blur-2xl" />
                            <div className="relative z-10">
                                <h3 className="text-2xl sm:text-3xl font-black tracking-tight mb-3">
                                    Welcome{profile.name ? `, ${profile.name.split(' ')[0]}` : ''}
                                </h3>
                                <p className="text-sm text-white/80 font-medium max-w-lg leading-relaxed">
                                    Manage your compliance documents and profile from this portal. Keep your documents up to date to maintain your active status.
                                </p>
                                {/* Profile Completion Bar */}
                                <div className="mt-6 max-w-md">
                                    <div className="flex justify-between mb-2">
                                        <span className="text-[10px] font-black uppercase tracking-widest text-white/60">Profile Completion</span>
                                        <span className="text-[10px] font-black uppercase tracking-widest text-white/90">{profile.profileCompletion || 0}%</span>
                                    </div>
                                    <div className="h-2 rounded-full bg-white/20 overflow-hidden">
                                        <div className="h-full rounded-full bg-gradient-to-r from-white/80 to-white transition-all duration-700" style={{ width: `${profile.profileCompletion || 0}%` }} />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Stats Grid */}
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                            {statCards.map((s, i) => (
                                <div key={i} className={`bg-white/60 dark:bg-slate-900/40 backdrop-blur-3xl rounded-[2rem] p-5 sm:p-6 border border-white/60 dark:border-white/10 shadow-sm dark:shadow-2xl relative overflow-hidden group hover:scale-[1.03] hover:${s.shadow} transition-all duration-300`}>
                                    <div className={`absolute -top-6 -right-6 w-20 h-20 bg-gradient-to-br ${s.gradient} opacity-[0.08] dark:opacity-[0.15] rounded-full blur-xl group-hover:opacity-[0.2] transition-opacity`} />
                                    <p className="text-3xl font-black text-slate-900 dark:text-white relative tracking-tight">
                                        {loading ? '...' : s.value}
                                    </p>
                                    <p className="text-[10px] text-slate-500 dark:text-slate-400 font-bold uppercase tracking-widest mt-1.5 relative">{s.label}</p>
                                </div>
                            ))}
                        </div>

                        {/* Quick Actions */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <Link to="/pa/documents" className="group relative bg-white/70 dark:bg-slate-900/60 backdrop-blur-3xl p-6 sm:p-8 rounded-[2.5rem] border border-white/60 dark:border-white/10 shadow-xl shadow-slate-200/50 dark:shadow-black/50 hover:shadow-2xl hover:shadow-violet-500/10 transition-all duration-500 overflow-hidden flex items-center gap-5">
                                <div className="w-14 h-14 rounded-[1.5rem] bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-white shadow-lg shadow-violet-500/20 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 flex-shrink-0">
                                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                                </div>
                                <div>
                                    <h4 className="text-lg font-black text-slate-900 dark:text-white tracking-tight">Upload Documents</h4>
                                    <p className="text-[11px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest mt-1">Manage your compliance files</p>
                                </div>
                            </Link>

                            <Link to="/pa/profile" className="group relative bg-white/70 dark:bg-slate-900/60 backdrop-blur-3xl p-6 sm:p-8 rounded-[2.5rem] border border-white/60 dark:border-white/10 shadow-xl shadow-slate-200/50 dark:shadow-black/50 hover:shadow-2xl hover:shadow-indigo-500/10 transition-all duration-500 overflow-hidden flex items-center gap-5">
                                <div className="w-14 h-14 rounded-[1.5rem] bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center text-white shadow-lg shadow-indigo-500/20 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 flex-shrink-0">
                                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                                </div>
                                <div>
                                    <h4 className="text-lg font-black text-slate-900 dark:text-white tracking-tight">Complete Profile</h4>
                                    <p className="text-[11px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest mt-1">Update your personal details</p>
                                </div>
                            </Link>
                        </div>

                        {/* Profile Summary Card */}
                        {!loading && (
                            <div className="bg-white/70 dark:bg-slate-900/60 backdrop-blur-3xl p-6 sm:p-8 rounded-[2.5rem] border border-white/60 dark:border-white/10 shadow-xl shadow-slate-200/50 dark:shadow-black/50">
                                <h3 className="text-lg font-black text-slate-900 dark:text-white uppercase tracking-tight mb-6 flex items-center gap-3">
                                    <span className="w-3 h-3 rounded-full bg-violet-500 shadow-[0_0_15px_rgba(139,92,246,0.5)]" />
                                    Profile Overview
                                </h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {[
                                        { label: 'Full Name', value: profile.name },
                                        { label: 'Email', value: profile.email },
                                        { label: 'Phone', value: profile.phone },
                                        { label: 'Date of Birth', value: profile.dateOfBirth ? new Date(profile.dateOfBirth).toLocaleDateString('en-GB') : 'Not set' },
                                        { label: 'Share Code', value: profile.shareCode || 'Not set' },
                                        { label: 'Address', value: profile.paAddress || 'Not set' },
                                    ].map((item, i) => (
                                        <div key={i} className="bg-slate-50/80 dark:bg-slate-800/30 p-4 rounded-2xl border border-slate-100 dark:border-white/5">
                                            <p className="text-[9px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-1.5">{item.label}</p>
                                            <p className={`text-sm font-bold truncate ${item.value && item.value !== 'Not set' ? 'text-slate-800 dark:text-white' : 'text-slate-300 dark:text-slate-600 italic'}`}>
                                                {item.value || 'Not set'}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Assignments Summary Card */}
                        {!loading && data?.assignedChildren && (
                            <div className="bg-white/70 dark:bg-slate-900/60 backdrop-blur-3xl p-6 sm:p-8 rounded-[2.5rem] border border-white/60 dark:border-white/10 shadow-xl shadow-slate-200/50 dark:shadow-black/50">
                                <h3 className="text-lg font-black text-slate-900 dark:text-white uppercase tracking-tight mb-6 flex items-center gap-3">
                                    <span className="w-3 h-3 rounded-full bg-emerald-500 shadow-[0_0_15px_rgba(16,185,129,0.5)]" />
                                    Assigned Routes & Children
                                </h3>
                                
                                {data.assignedRoutes.length === 0 && data.assignedChildren.length === 0 ? (
                                    <div className="text-center py-10 bg-slate-50/50 dark:bg-slate-800/30 rounded-2xl border border-slate-100 dark:border-white/5">
                                        <p className="text-sm font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">No active assignments</p>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                        {/* Routes List */}
                                        <div className="space-y-4">
                                            <h4 className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest pl-1">Your Routes</h4>
                                            {data.assignedRoutes.length > 0 ? data.assignedRoutes.map((route, i) => (
                                                <div key={i} className="bg-slate-50/80 dark:bg-slate-800/30 p-5 rounded-2xl border border-slate-100 dark:border-white/5 flex flex-col gap-3 group hover:border-indigo-200 dark:hover:border-indigo-500/30 transition-colors">
                                                    <div className="flex items-start justify-between">
                                                        <div>
                                                            <div className="flex items-center gap-2 mb-1">
                                                                <p className="text-sm font-bold text-slate-800 dark:text-white">{decodeHtml(route.name)}</p>
                                                                <span className="px-2 py-0.5 rounded-full bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 text-[8px] font-black uppercase tracking-widest border border-indigo-100 dark:border-indigo-500/20">
                                                                    {route.type}
                                                                </span>
                                                            </div>
                                                            <p className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">{decodeHtml(route.schoolId?.name) || 'No School'}</p>
                                                        </div>
                                                        <div className="text-right">
                                                            <p className="text-sm font-black text-slate-900 dark:text-white">
                                                                {route.scheduledTime || '--:--'}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    {route.mapLink && (
                                                        <div className="pt-3 border-t border-slate-200/50 dark:border-slate-700/50">
                                                            <a href={route.mapLink} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 transition-colors">
                                                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                                                                View Route Map
                                                            </a>
                                                        </div>
                                                    )}
                                                </div>
                                            )) : (
                                                <p className="text-xs text-slate-400 italic pl-1">No specific routes assigned</p>
                                            )}
                                        </div>

                                        {/* Children List */}
                                        <div className="space-y-4">
                                            <h4 className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest pl-1">Assigned Children</h4>
                                            {data.assignedChildren.length > 0 ? data.assignedChildren.map((child, i) => (
                                                <div key={i} className="bg-slate-50/80 dark:bg-slate-800/30 p-4 rounded-2xl border border-slate-100 dark:border-white/5 flex items-center justify-between">
                                                    <div>
                                                        <p className="text-sm font-bold text-slate-800 dark:text-white">{decodeHtml(child.name)}</p>
                                                        <p className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mt-1">{decodeHtml(child.schoolId?.name) || 'No School'}</p>
                                                    </div>
                                                    <span className="px-3 py-1 rounded-full bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-[9px] font-black uppercase tracking-widest border border-emerald-100 dark:border-emerald-500/20">
                                                        Assigned
                                                    </span>
                                                </div>
                                            )) : (
                                                <p className="text-xs text-slate-400 italic pl-1">No children assigned</p>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </main>
            </div>

            <BottomNav items={paMenuItems} />
        </div>
    );
};

export default PADashboard;
